import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, Shield } from "lucide-react";
import { StatsOverview } from "@/components/statistics/StatsOverview";
import { PopularRanges } from "@/components/statistics/PopularRanges";
import { NumberFrequency } from "@/components/statistics/NumberFrequency";
import { GenerationTimeline } from "@/components/statistics/GenerationTimeline";
import { ActiveDraws } from "@/components/admin/ActiveDraws";
import { SessionHistory } from "@/components/admin/SessionHistory";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AdminPortal = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forceLastNumber, setForceLastNumber] = useState<string>("");
  const [endingDigits, setEndingDigits] = useState<string>("");
  
  // Queue management state
  const [queuedNumbers, setQueuedNumbers] = useState<any[]>([]);
  const [newQueueNumber, setNewQueueNumber] = useState<string>("");
  const [newQueueMin, setNewQueueMin] = useState<string>("1");
  const [newQueueMax, setNewQueueMax] = useState<string>("100");
  
  // Statistics state
  const [stats, setStats] = useState({
    totalGenerations: 0,
    totalNumbers: 0,
    uniqueUsers: 0,
    avgPerGeneration: 0,
    popularRanges: [] as any[],
    numberFrequencies: {} as Record<number, number>,
    timeline: [] as any[]
  });

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate("/");
      toast({
        title: "Access Denied",
        description: "You don't have permission to access this page.",
        variant: "destructive"
      });
    }
  }, [isAdmin, authLoading, navigate, toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchAdminSettings();
      fetchStatistics();
      fetchQueuedNumbers();
    }
  }, [isAdmin]);

  const fetchAdminSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('force_last_number, ending_digits')
        .eq('key', 'random_numbers')
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setForceLastNumber(data.force_last_number?.toString() || "");
        setEndingDigits(data.ending_digits || "");
      }
    } catch (error) {
      console.error('Error fetching admin settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQueuedNumbers = async () => {
    try {
      const { data, error } = await supabase
        .from('queued_numbers')
        .select('*')
        .eq('used', false)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setQueuedNumbers(data || []);
    } catch (error) {
      console.error('Error fetching queued numbers:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const { data, error } = await supabase
        .from('number_generations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const totalGenerations = data?.length || 0;
      const totalNumbers = data?.reduce((sum, gen) => sum + gen.count, 0) || 0;
      const uniqueUsers = new Set(data?.map(gen => gen.ip_address)).size;
      const avgPerGeneration = totalNumbers / totalGenerations || 0;

      // Popular ranges
      const rangeCounts: Record<string, number> = {};
      data?.forEach(gen => {
        const range = `${gen.min_value}-${gen.max_value}`;
        rangeCounts[range] = (rangeCounts[range] || 0) + 1;
      });
      
      const maxCount = Math.max(...Object.values(rangeCounts));
      const popularRanges = Object.entries(rangeCounts)
        .map(([range, count]) => ({
          range,
          count,
          percentage: (count / maxCount) * 100
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Number frequencies
      const frequencies: Record<number, number> = {};
      data?.forEach(gen => {
        gen.generated_numbers.forEach(num => {
          frequencies[num] = (frequencies[num] || 0) + 1;
        });
      });

      // Timeline
      const timelineCounts: Record<string, number> = {};
      data?.forEach(gen => {
        const date = new Date(gen.created_at).toLocaleDateString();
        timelineCounts[date] = (timelineCounts[date] || 0) + 1;
      });

      const timeline = Object.entries(timelineCounts)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      setStats({
        totalGenerations,
        totalNumbers,
        uniqueUsers,
        avgPerGeneration,
        popularRanges,
        numberFrequencies: frequencies,
        timeline
      });
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleAddToQueue = async () => {
    if (!newQueueNumber || !newQueueMin || !newQueueMax) {
      toast({
        title: "Invalid Input",
        description: "Please fill in all fields.",
        variant: "destructive"
      });
      return;
    }

    const number = parseInt(newQueueNumber);
    const min = parseInt(newQueueMin);
    const max = parseInt(newQueueMax);

    if (number < min || number > max) {
      toast({
        title: "Invalid Range",
        description: "Number must be within the specified range.",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('queued_numbers')
        .insert({
          number,
          min_range: min,
          max_range: max,
          used: false
        });

      if (error) throw error;

      toast({
        title: "Added to Queue",
        description: `Number ${number} queued for range ${min}-${max}.`
      });

      setNewQueueNumber("");
      fetchQueuedNumbers();
    } catch (error) {
      console.error('Error adding to queue:', error);
      toast({
        title: "Error",
        description: "Failed to add number to queue.",
        variant: "destructive"
      });
    }
  };

  const handleRemoveFromQueue = async (id: string) => {
    try {
      const { error } = await supabase
        .from('queued_numbers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Removed from Queue",
        description: "Number removed successfully."
      });

      fetchQueuedNumbers();
    } catch (error) {
      console.error('Error removing from queue:', error);
      toast({
        title: "Error",
        description: "Failed to remove number.",
        variant: "destructive"
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('admin_settings')
        .upsert({
          key: 'random_numbers',
          force_last_number: forceLastNumber ? parseInt(forceLastNumber) : null,
          ending_digits: endingDigits || null
        }, {
          onConflict: 'key'
        });

      if (error) throw error;

      toast({
        title: "Settings Saved",
        description: "Game rigging settings have been updated successfully."
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-8">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-4xl font-bold text-foreground">Admin Portal</h1>
            <p className="text-muted-foreground mt-1">Control game settings and view statistics</p>
          </div>
        </div>

        <Tabs defaultValue="active" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="active">Active Draws</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="queue">Rigging Queue</TabsTrigger>
            <TabsTrigger value="settings">Global Settings</TabsTrigger>
            <TabsTrigger value="stats">Statistics</TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="space-y-4">
            <ActiveDraws />
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            <SessionHistory />
          </TabsContent>

          <TabsContent value="queue" className="space-y-4">
            {/* Rigging Queue */}
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Shield className="h-6 w-6" />
                Rigging Queue (Priority)
              </h2>
              
              <div className="space-y-4 mb-6">
                <p className="text-sm text-muted-foreground">
                  Add specific numbers to force for upcoming draws. These take priority over global settings.
                </p>
                
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label htmlFor="queueNumber">Number</Label>
                    <Input
                      id="queueNumber"
                      type="number"
                      value={newQueueNumber}
                      onChange={(e) => setNewQueueNumber(e.target.value)}
                      placeholder="e.g., 42"
                    />
                  </div>
                  <div>
                    <Label htmlFor="queueMin">Min</Label>
                    <Input
                      id="queueMin"
                      type="number"
                      value={newQueueMin}
                      onChange={(e) => setNewQueueMin(e.target.value)}
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="queueMax">Max</Label>
                    <Input
                      id="queueMax"
                      type="number"
                      value={newQueueMax}
                      onChange={(e) => setNewQueueMax(e.target.value)}
                      placeholder="100"
                    />
                  </div>
                </div>

                <Button onClick={handleAddToQueue} className="w-full">
                  Add to Queue
                </Button>
              </div>

              {queuedNumbers.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold">Current Queue ({queuedNumbers.length})</h3>
                  <div className="space-y-2">
                    {queuedNumbers.map((item, index) => (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div>
                          <span className="font-mono font-bold text-lg">{index + 1}. Number {item.number}</span>
                          <span className="text-sm text-muted-foreground ml-2">
                            (Range: {item.min_range}-{item.max_range})
                          </span>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleRemoveFromQueue(item.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            {/* Game Rigging Controls */}
            <Card className="p-6">
              <h2 className="text-2xl font-bold text-foreground mb-4">Global Fallback Settings</h2>
              <div className="space-y-4 max-w-md">
                <div>
                  <Label htmlFor="forceLastNumber">Force Last Number</Label>
                  <Input
                    id="forceLastNumber"
                    type="number"
                    placeholder="e.g., 7"
                    value={forceLastNumber}
                    onChange={(e) => setForceLastNumber(e.target.value)}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Used when queue is empty and all numbers in range have been used
                  </p>
                </div>

                <div>
                  <Label htmlFor="endingDigits">Ending Digits</Label>
                  <Input
                    id="endingDigits"
                    type="text"
                    placeholder="e.g., 7 or 3,7"
                    value={endingDigits}
                    onChange={(e) => setEndingDigits(e.target.value)}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Prioritize numbers ending with these digits (when queue is empty)
                  </p>
                </div>

                <Button onClick={handleSave} disabled={saving} className="w-full">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="stats" className="space-y-4">
            {/* Statistics */}
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-foreground">Game Statistics</h2>
              
              <StatsOverview
                totalGenerations={stats.totalGenerations}
                totalNumbers={stats.totalNumbers}
                uniqueUsers={stats.uniqueUsers}
                avgPerGeneration={stats.avgPerGeneration}
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PopularRanges ranges={stats.popularRanges} />
                <GenerationTimeline data={stats.timeline} />
              </div>

              <NumberFrequency frequencies={stats.numberFrequencies} />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AdminPortal;
